import { useEffect } from 'react';
import { UnstyledButton } from '@mantine/core';
import type { Job } from '@nelnela/contracts';
import { jobPillModel } from '../../lib/useJob';

/** how long "Cleaned up ✓" lingers before the pill asks to be dismissed */
export const JOB_PILL_DONE_MS = 4000;

const PILL_CSS = `
@keyframes nn-pill-pulse { 0%,100% { opacity: .35 } 50% { opacity: 1 } }
.nn-jobpill-dot { animation: nn-pill-pulse 1.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .nn-jobpill-dot { animation: none; } }
`;

export interface JobPillProps {
  job: Job | null;
  /** error tone: the Retry action (re-POST the job; the caller swaps in the new job id) — omit
   *  when the current learner may not restart this job (no Retry is shown) */
  onRetry?: () => void;
  /** done tone: called once ~4 s after "… ✓" appears — the caller hides the pill (job → null) */
  onDismiss?: () => void;
  /** the poller lost the job (5 failed polls, backing off): a calm "retrying" line replaces the % */
  stalled?: boolean;
}

/**
 * The Watch header's job pill (frame 1g token, reading-surface-spec §7): sky pulse + thin
 * progress fill while queued/running, coral-red tint with Retry on error, "✓" for ~4 s on done.
 * Renders nothing for no job / inert. ≤ 28 px tall; truncates to fit a phone header row.
 */
export function JobPill({ job, onRetry, onDismiss, stalled }: JobPillProps): JSX.Element | null {
  const m = jobPillModel(job, { stalled });
  const done = m?.tone === 'done';
  const jobId = job?.id ?? null;
  useEffect(() => {
    if (!done || !onDismiss) return;
    const t = setTimeout(onDismiss, JOB_PILL_DONE_MS);
    return () => clearTimeout(t);
    // re-arm per job, not per parent render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done, jobId]);
  if (!m) return null;

  const sky = 'light-dark(#3c76b8, #4f8fd9)';
  const skyText = 'light-dark(#2f5f96, #8fb4e0)';
  const coral = 'light-dark(#b0433b, #e0938c)';
  const border =
    m.tone === 'error'
      ? 'light-dark(rgba(176,67,59,.4), rgba(224,100,92,.35))'
      : 'var(--nn-hairline)';
  const color = m.tone === 'error' ? coral : m.tone === 'done' ? 'var(--nn-muted)' : skyText;

  return (
    <span
      role="status"
      aria-live="polite"
      title={stalled && m.tone === 'progress' ? m.text : (job?.message ?? m.text)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: 24,
        maxWidth: '100%',
        padding: '0 10px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: 'var(--nn-panel-2)',
        color,
        fontSize: 11,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        minWidth: 0,
        flexShrink: 1,
      }}
    >
      <style>{PILL_CSS}</style>
      {m.tone === 'progress' && (
        <span
          className="nn-jobpill-dot"
          aria-hidden
          style={{ width: 6, height: 6, borderRadius: '50%', background: sky, flexShrink: 0 }}
        />
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{m.text}</span>
      {m.retry && onRetry && (
        <>
          <span aria-hidden style={{ color: 'var(--nn-faint)' }}>
            ·
          </span>
          <UnstyledButton
            onClick={onRetry}
            aria-label="Retry"
            style={{
              color: 'var(--nn-link)',
              fontSize: 11,
              lineHeight: 1,
              // the pill is 24 px; pad the tap target vertically to ≥40 px without growing the pill
              padding: '8px 2px',
              margin: '-8px -2px',
              flexShrink: 0,
            }}
          >
            Retry
          </UnstyledButton>
        </>
      )}
      {m.tone === 'progress' && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 2,
            width: `${Math.round(m.progress * 100)}%`,
            borderRadius: 999,
            background: sky,
            transition: 'width .4s ease',
          }}
        />
      )}
    </span>
  );
}
